import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination';
import clsx from 'clsx';
import { Typography } from '../typography';
import { usePathname, useSearchParams } from 'next/navigation';
import { generatePagination } from '@/utils';

interface Props {
	totalPages: number;
	className?: string;
}

export const PaginationCustom: React.FC<Props> = ({
	className,
	totalPages = 4,
}) => {
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const currentPage = Number(searchParams.get('page')) || 1;
	const allPages = generatePagination(currentPage, totalPages);

	const createPageURL = (pageNumber: number | string): string => {
		const number = Number(pageNumber);
		const params = new URLSearchParams(searchParams);
		if (number > 0 && number <= totalPages) {
			params.set('page', pageNumber.toString());
		}
		return `${pathname}?${params.toString()}`;
	};

	const currentColor =
		pathname.includes('clientapp') || pathname.includes('lawyerapp')
			? 'text-green-60'
			: 'text-purple-150';
	const currentBg =
		pathname.includes('clientapp') || pathname.includes('lawyerapp')
			? 'bg-green-secondary'
			: 'bg-purple-15';

	return (
		<Pagination className={className}>
			<PaginationContent className="gap-2">
				<PaginationItem
					className={clsx('', {
						[currentColor]: currentPage - 1 > 0,
					})}
				>
					<PaginationPrevious
						href={createPageURL(currentPage - 1)}
						className={clsx('px-2', {
							'pointer-events-none': currentPage - 1 === 0,
						})}
					/>
				</PaginationItem>
				{allPages.map((page, index) => (
					<PaginationItem key={index} className="">
						<PaginationLink
							href={createPageURL(page)}
							className={clsx('bg-white rounded-lg text-xs', {
								[currentBg]: page === currentPage,
								'pointer-events-none': page === '...',
							})}
						>
							<Typography
								type="semibold-12"
								className={clsx('opacity-60', {
									[currentColor]: page === currentPage,
								})}
							>
								{page}
							</Typography>
						</PaginationLink>
					</PaginationItem>
				))}
				<PaginationItem
					className={clsx('', {
						[currentColor]: currentPage + 1 <= totalPages,
					})}
				>
					<PaginationNext
						href={createPageURL(currentPage + 1)}
						className={clsx('px-2', {
							'pointer-events-none': currentPage + 1 > totalPages,
						})}
					/>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	);
};
